import { SingleBuildingContext } from "features/dt-basic-setup/single-building/context/AddSingleBuildingContext";
import { FileWithInfo, isFileFromApi } from "features/dt-basic-setup/single-building/model/types";
import React, { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { createId } from "utils/general";

import { StepColumn } from "../layout/StepColumn";
import { useSummaryStep } from "./hooks/useSummaryStep";
import { SummaryMixConfigurationModal } from "./SummaryMixConfigurationModal";
import { SummaryStepDetailsForm } from "./SummaryStepDetailsForm";
import { SummaryStepImageUploadAndContentSummary } from "./SummaryStepImageUploadAndContentSummary";

export const SummaryStep = () => {
  const { t } = useTranslation();
  const { formData, setFormData } = useContext(SingleBuildingContext);
  const {
    details,
    handleSaveMixConfiguration,
    hasInvalidMixTotal,
    mixConfiguration,
    mixDraft,
    mixError,
    mixModalOpen,
    mixTotal,
    openMixConfigurationModal,
    setMixModalOpen,
    updateDetailsField,
    updateMixValue,
  } = useSummaryStep();

  const handleImageChange = useCallback(
    (file: File | undefined) => {
      if (!file) return;
      setFormData((prev) => ({
        ...prev,
        images: [...(prev.images ?? []), { file, id: createId(), source: "local" }],
      }));
    },
    [setFormData]
  );

  const handleRemoveImage = useCallback(
    (image: FileWithInfo) => {
      setFormData((prev) => ({
        ...prev,
        deletedAPIImages: isFileFromApi(image) ? [...(prev.deletedAPIImages ?? []), image] : prev.deletedAPIImages,
        images: (prev.images ?? []).filter((f: FileWithInfo) => f.id !== image.id),
      }));
    },
    [setFormData]
  );

  return (
    <>
      <StepColumn
        subtitle={t("AddBuilding.summaryStep.summarySubtitle") as string}
        title={t("AddBuilding.summaryStep.summaryTitle")}
      >
        <SummaryStepImageUploadAndContentSummary
          handleImageChange={handleImageChange}
          handleRemoveImage={handleRemoveImage}
          images={formData.images ?? []}
        />
      </StepColumn>
      <StepColumn
        subtitle={t("AddBuilding.summaryStep.detailsSubtitle") as string}
        title={t("AddBuilding.summaryStep.detailsTitle")}
      >
        <SummaryStepDetailsForm
          detailsBuildingType={details?.building_type}
          mixConfiguration={mixConfiguration}
          onOpenMixConfiguration={openMixConfigurationModal}
          updateDetailsField={updateDetailsField}
        />
      </StepColumn>
      <SummaryMixConfigurationModal
        handleSaveMixConfiguration={handleSaveMixConfiguration}
        hasInvalidMixTotal={hasInvalidMixTotal}
        mixDraft={mixDraft}
        mixError={mixError}
        mixModalOpen={mixModalOpen}
        mixTotal={mixTotal}
        setMixModalOpen={setMixModalOpen}
        updateMixValue={updateMixValue}
      />
    </>
  );
};
